<template>
	<ion-card class="m-0">
		<ion-card-header>
			<div class="flex items-start justify-between">
				<div class="flex-1">
					<ion-card-title>
						{{ plantingFormData.name || "" }}
					</ion-card-title>
					<ion-card-subtitle v-if="ranchName">
						{{ ranchName }}
					</ion-card-subtitle>
				</div>
				<div class="flex gap-1">
					<!-- Action Buttons -->
					<ion-button fill="clear" size="small" @click="emit('edit')">
						<ion-icon :icon="pencilOutline" />
					</ion-button>
					<ion-button
						fill="clear"
						size="small"
						color="danger"
						@click="emit('remove')"
					>
						<ion-icon :icon="trashOutline" />
					</ion-button>
				</div>
			</div>
		</ion-card-header>

		<ion-card-content>
			<!-- Lots and Sublot -->
			<p
				v-if="lotNames || plantingFormData.sublot"
				class="mb-1 text-sm text-gray-600"
			>
				<span v-if="lotNames">Lot: {{ lotNames }}</span>
				<span v-if="lotNames && plantingFormData.sublot"> • </span>
				<span v-if="plantingFormData.sublot">
					Sublot: {{ plantingFormData.sublot }}
				</span>
			</p>

			<!-- Crop (if custom name) -->
			<p v-if="cropName" class="mb-2 text-gray-600">
				{{ cropName }}
			</p>

			<!-- Primary metrics -->
			<p class="ion-text-wrap">
				<span v-if="plantingFormData.acres" class="font-semibold">
					{{ plantingFormData.acres }} acres
				</span>
				<span v-if="plantingFormData.plantDate">
					<span v-if="plantingFormData.acres"> • </span>
					Plant {{ formatDate(plantingFormData.plantDate) }}
				</span>
				<span v-if="plantingFormData.wetDate">
					<span
						v-if="
							plantingFormData.acres || plantingFormData.plantDate
						"
					>
						•
					</span>
					Wet {{ formatDate(plantingFormData.wetDate) }}
				</span>
				<span v-if="plantingFormData.harvestDates?.[0]">
					• Harvest
					{{ formatDate(plantingFormData.harvestDates?.[0]) }}
				</span>
			</p>

			<!-- Status/Errors/Warnings -->
			<div v-if="!hasIssues" class="my-2">
				<p class="flex items-center gap-1 text-green-600">
					<ion-icon :icon="checkmarkCircle" />
					<span>No issues</span>
				</p>
			</div>
			<div v-else class="mt-2 space-y-1">
				<!-- Errors (blocking) - display as list -->
				<p
					v-for="(error, index) in errorList"
					:key="`error-${index}`"
					class="flex items-center gap-1 text-red-600"
				>
					<ion-icon
						:icon="closeCircleOutline"
						class="flex-shrink-0"
					/>
					<span>{{ error }}</span>
				</p>
				<!-- Warnings (non-blocking) - display as list -->
				<p
					v-for="(warning, index) in warningList"
					:key="`warning-${index}`"
					class="flex items-center gap-1 text-orange-600"
				>
					<ion-icon :icon="warningOutline" class="flex-shrink-0" />
					<span>{{ warning }}</span>
				</p>
			</div>
		</ion-card-content>
	</ion-card>
</template>

<script setup lang="ts">
	import { computed } from "vue";
	import {
		IonButton,
		IonIcon,
		IonCard,
		IonCardHeader,
		IonCardTitle,
		IonCardSubtitle,
		IonCardContent,
	} from "@ionic/vue";
	import {
		pencilOutline,
		trashOutline,
		checkmarkCircle,
		warningOutline,
		closeCircleOutline,
	} from "ionicons/icons";
	import { useCrops } from "seedgreen-shared/queries/crop";
	import { useRanches } from "seedgreen-shared/queries/ranch";
	import { useLots } from "seedgreen-shared/queries/lot";
	import dayjs from "dayjs";
	import { PlantingFormData } from "@/validation/planting.schema";

	const props = defineProps<{
		plantingFormData: PlantingFormData;
		errorMessage?: string;
	}>();

	const emit = defineEmits<{
		edit: [];
		remove: [];
	}>();

	const { cropsById } = useCrops();
	const { ranchesById } = useRanches();
	const { lotsById } = useLots();

	const cropName = computed(() => {
		const id = props.plantingFormData.cropId;
		if (!id) return "";
		return cropsById.value?.[id]?.fullName || "Unknown Crop";
	});

	const ranchName = computed(() => {
		const id = props.plantingFormData.ranchId;
		if (!id) return "";
		return ranchesById.value?.[id]?.fullName || "";
	});

	const lotNames = computed(() => {
		if (!props.plantingFormData.lotIds?.length) return "";
		const foundLots = props.plantingFormData.lotIds
			.map((lotId) => lotsById.value[lotId]?.name)
			.filter(Boolean);
		return foundLots.join(", ");
	});

	/**
	 * Split error message string into individual errors.
	 * Errors from import are semicolon-separated.
	 */
	const errorList = computed(() => {
		if (!props.errorMessage) return [];
		return props.errorMessage
			.split(";")
			.map((msg) => msg.trim())
			.filter((msg) => msg.length > 0);
	});

	/**
	 * Collect all non-blocking warnings.
	 */
	const warningList = computed(() => {
		const warnings: string[] = [];

		// Add boundary warning
		if (!props.plantingFormData.latestBoundary) {
			warnings.push("No planting boundary added");
		}

		// Could add more warnings here in the future

		return warnings;
	});

	const hasIssues = computed(() => {
		return errorList.value.length > 0 || warningList.value.length > 0;
	});

	function formatDate(dateString: string | undefined): string {
		if (!dateString) return "";
		return dayjs(dateString).format("M/D/YYYY");
	}
</script>
